import { AfterViewChecked, Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core'
import { NgbActiveModal, NgbDateStruct, NgbModal } from '@ng-bootstrap/ng-bootstrap'
import * as dayjs from 'dayjs'
import * as utc from 'dayjs/plugin/utc'
import * as timezone from 'dayjs/plugin/timezone'
import { ScheduleDetails, ScheduleEvent } from '../schedule'
import { ProgramsService } from '../../programs/programs.service'
import { ReportProblemService } from './report-problem.service'
import { ConfirmationModalComponent } from '../../../components/confirmation-modal/confirmation-modal.component'
import { forkJoin, of, Subject } from 'rxjs'
import { finalize, takeUntil } from 'rxjs/operators'
import { AppConstants } from 'src/app/app.constants'

dayjs.extend(utc)
dayjs.extend(timezone)

@Component({
    selector: 'app-report-problem-modal',
    templateUrl: './report-problem-modal.component.html',
    styleUrls: ['./report-problem-modal.component.scss'],
})
export class ReportProblemModalComponent implements OnInit, AfterViewChecked, OnDestroy {
    @Input() channelId: string
    @Input() selectedDate: NgbDateStruct
    @Input() timezone = 'GMT'
    @Input() schedules: ScheduleDetails[] = []

    lang: string
    payload: ReportProblem
    rows: ReportProblemRow[] = []
    errorTypes: any[] = []
    errorsByEventId: Map<string, any> = new Map()
    isLoading = true
    loadFailed = false
    isSaving = false
    @ViewChildren('commentInput') commentInputs!: QueryList<ElementRef<HTMLTextAreaElement>>
    private readonly destroy$ = new Subject<void>()
    private resizeCommentsPending = false

    constructor(
        private activeModal: NgbActiveModal,
        private programsService: ProgramsService,
        private reportProblemService: ReportProblemService,
        private modalService: NgbModal,
    ) { }

    ngOnInit() {
        this.payload = {
            channelId: this.channelId,
            events: [],
            eventIdsToDelete: []
        }
        this.rows = this.schedules?.length ? this.mapScheduleEvents(this.schedules) : []
        this.loadReportData()
    }

    ngAfterViewChecked() {
        if (this.resizeCommentsPending && !this.isLoading && this.commentInputs?.length) {
            this.commentInputs.forEach(input => this.resizeComment(input.nativeElement))
            this.resizeCommentsPending = false
        }
    }

    ngOnDestroy() {
        this.destroy$.next()
        this.destroy$.complete()
    }

    private loadReportData() {
        const eventIds = Array.from(new Set(this.rows.map(row => row.eventId).filter(id => !!id)))
        this.lang = location.href.includes('ssla') ? 'es' : 'pt'
        this.isLoading = true
        this.loadFailed = false
        forkJoin({
            types: this.reportProblemService.getScheduleErrorTypes(),
            errors: eventIds.length ? this.reportProblemService.getScheduleErrors(eventIds) : of([]),
        }).pipe(
            takeUntil(this.destroy$),
            finalize(() => {
                this.isLoading = false
                this.resizeCommentsPending = true
            }),
        ).subscribe(
            (data: any) => {
                // Handle list error types for dropdown selection
                this.errorTypes = (data.types?.response || []).map((type: any) => ({
                    id: type._id,
                    label: type.descriptions?.find((d: any) => d.lang === this.lang)?.value ?? type.descriptions?.[0]?.value ?? ''
                })).filter((type: any) => !!type.id && !!type.label)
                // Handle list of existing errors for each event
                const errors: any[] = Array.isArray(data.errors) ? data.errors : data.errors?.response || []
                this.errorsByEventId = new Map(errors.map((error: any) => [error.eventId, error]))
                this.rows.forEach(row => {
                    const error = this.errorsByEventId.get(row.eventId)
                    if (error) {
                        row.errorType = error.errorType || ''
                        row.comment = error.comment || ''
                    }
                })
            },
            () => {
                this.loadFailed = true
                this.showAlert(AppConstants.ERROR, 'Failed to load schedule error data. Please close and reopen this popup before saving.')
            },
        )
    }

    clearRow(row: ReportProblemRow, commentInput: HTMLTextAreaElement) {
        row.errorType = ''
        row.comment = ''
        commentInput.style.height = '31px'
    }

    resizeComment(commentInput: HTMLTextAreaElement) {
        commentInput.style.height = 'auto'
        commentInput.style.height = `${commentInput.scrollHeight}px`
    }

    cancel() {
        this.activeModal.dismiss('cancel')
    }

    isErrorTypeRequired(row: ReportProblemRow): boolean {
        return !!row.comment?.trim() && !row.errorType?.trim()
    }

    saveErrors(): void {
        if (this.rows.some(row => this.isErrorTypeRequired(row))) {
            this.showAlert(
                AppConstants.WARNING,
                'Please select an error type for each event with a comment.'
            )
            return
        }

        const eventIdsToDelete: string[] = []
        const eventsToSave: any[] = []

        this.rows.forEach((row) => {
            const existingError = this.errorsByEventId.get(row.eventId)
            const isOldEvent = !!existingError

            const errorType = row.errorType?.trim() || null
            const comment = row.comment?.trim() || null
            const publishedTitle = row.publishedTitle?.trim() || null

            const originalErrorType = existingError?.errorType || null
            const originalComment = existingError?.comment || null

            // Delete
            if (isOldEvent && !errorType && !comment) {
                eventIdsToDelete.push(row.eventId)
                return
            }

            // No change
            if (
                isOldEvent &&
                errorType === originalErrorType &&
                comment === originalComment
            ) {
                return
            }

            // Create / Update
            if (errorType && row.scheduleId) {
                eventsToSave.push({
                    scheduleId: row.scheduleId,
                    version: row.version,
                    eventId: row.eventId,
                    eventStartDate: row.eventStartDate,
                    publishedTitle,
                    errorType,
                    comment,
                })
            }
        })

        if (!eventsToSave.length && !eventIdsToDelete.length) {
            this.showAlert(AppConstants.WARNING, 'No changes detected.')
            return
        }

        this.payload.events = eventsToSave
        this.payload.eventIdsToDelete = eventIdsToDelete

        this.isSaving = true

        this.reportProblemService
            .saveErrors(this.payload)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isSaving = false
                })
            )
            .subscribe(
                (response) => {
                    const result = response.response

                    const messages: string[] = []

                    if (result.created > 0) {
                        messages.push(`${result.created} error(s) created`)
                    }

                    if (result.updated > 0) {
                        messages.push(`${result.updated} error(s) updated`)
                    }

                    if (result.deleted?.length > 0) {
                        messages.push(`${result.deleted.length} error(s) deleted`)
                    }

                    if (result.notFound?.length > 0) {
                        messages.push(`${result.notFound.length} event(s) not found`)
                    }

                    if (result.failed?.length > 0) {
                        messages.push(`${result.failed.length} operation(s) failed`)
                    }

                    const failedDetails = result.failed?.length
                        ? result.failed
                            .map(
                                (item) =>
                                    `${item.eventId}: ${item.error}`
                            )
                            .join('\n')
                        : ''

                    const hasWarnings =
                        result.notFound?.length > 0 ||
                        result.failed?.length > 0

                    const message =
                        `${messages.join('\n')}` +
                        (failedDetails
                            ? `\n\nFailed details:\n${failedDetails}`
                            : '')

                    this.showAlert(
                        hasWarnings ? AppConstants.WARNING : AppConstants.SUCCESS,
                        message || 'Errors saved successfully'
                    )

                    if (!hasWarnings) {
                        this.activeModal.close(true)
                    }
                },
                () => {
                    this.showAlert(AppConstants.ERROR, 'Failed to save errors')
                }
            )
    }

    showAlert(type: string, message: string) {
        const modalRef = this.modalService.open(ConfirmationModalComponent, {
            size: 'sm',
            centered: true,
            backdrop: 'static',
        })
        modalRef.componentInstance.title = type
        modalRef.componentInstance.message = message
        modalRef.componentInstance.isAlert = true
    }

    private mapScheduleEvents(data: ScheduleDetails[] | ScheduleDetails): ReportProblemRow[] {
        const schedules = Array.isArray(data) ? data : data ? [data] : []
        const rows: ReportProblemRow[] = []

        schedules.forEach(schedule => {
            const displayedEvents = schedule.displayedEvent({ value: this.timezone }, this.selectedDate) || []
            displayedEvents.forEach(event => rows.push(this.toRow(event, schedule.scheduleId, schedule.version)))
        })

        return rows
    }

    private toRow(event: ScheduleEvent, scheduleId: string, version: number): ReportProblemRow {
        const title = this.programsService.getFirstTitleDescValue(event.programDetails, 'titles', true)

        return {
            scheduleId,
            version,
            eventId: event.id,
            programId: event.programId || '',
            time: event.startDate ? dayjs.utc(event.startDate).tz(this.timezone).format('HH:mm') : '--:--',
            publishedTitle: title,
            errorType: '',
            comment: '',
            eventStartDate: event.startDate || ''
        }
    }
}